import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { CONSTANTS, createData, dataUtils, timeUtils } from '../composables/utils';
import { normalizeRecords } from '../lib/cardOrder';
import { listGroups } from '../services/groupsService';
import { getRealTime } from './realtimeApi';

export const realtimeKeys = {
    all: ['realtime'] as const,
    lists: () => [...realtimeKeys.all, 'list'] as const,
    list: (archived: boolean) => [...realtimeKeys.lists(), { archived }] as const,
    groups: () => [...realtimeKeys.all, 'groups'] as const,
};

// The last week up to now, worked out per request so each poll reaches the present.
const fetchRealtimeRecords = async (archived: boolean): Promise<any[]> => {
    const { start, end } = timeUtils.getDefaultTimeRange();
    const data = await getRealTime(dataUtils.formatTimeForAPI(start), dataUtils.formatTimeForAPI(end), archived);
    return Array.isArray(data?.records) ? data.records : [];
};

/** Key and fetcher for one tab's raw records, for prefetching or `fetchQuery`. */
export const realtimeListQuery = (archived: boolean) => ({
    queryKey: realtimeKeys.list(archived),
    queryFn: () => fetchRealtimeRecords(archived),
});

/**
 * One tab's device cards, polled every minute while the browser tab is visible.
 * `structuralSharing` is off so `select` reruns each poll: `connected` depends on the clock.
 */
export const useRealtimeQuery = ({ archived, tempUnit }: { archived: boolean; tempUnit: string }) => {
    const select = useCallback(
        (records: any[]) => normalizeRecords(records.map((record, index) => createData(record, index, tempUnit))),
        [tempUnit]
    );

    return useQuery({
        ...realtimeListQuery(archived),
        select,
        refetchInterval: CONSTANTS.UPDATE_INTERVAL,
        structuralSharing: false,
    });
};

/** The account's custom device groups (the virtual "All Devices" isn't one of them). */
export const useGroupsQuery = () =>
    useQuery({
        queryKey: realtimeKeys.groups(),
        queryFn: listGroups,
    });
